import useJwt from '@/auth/jwt/useJwt'

/**
 * Return if user is logged in
 * This is completely up to you and how you want to store the token in your frontend application
 * e.g. If you are using cookies to store the application please update this function
 */
// eslint-disable-next-line arrow-body-style
export const isUserLoggedIn = () => {
  return (
    localStorage.getItem('userData') &&
    localStorage.getItem(useJwt.jwtConfig.storageTokenKeyName)
  )
}

export const getUserData = () => JSON.parse(localStorage.getItem('userData'))

export const setUserData = value => {
  localStorage.setItem('userData', JSON.stringify(value))
}

export const getPartnerData = key => {
  try {
    const item = JSON.parse(localStorage.getItem(key))
    if (!item) {
      return null // Item doesn't exist in localStorage
    }
    const now = new Date()
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key) // Remove the item if it has expired
      return null
    }
    return item.value
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error)
    return null
  }
}

export const setPartnerData = (key, value, secondsToExpire) => {
  try {
    const now = new Date()
    const item = {
      value,
      expiry: now.getTime() + secondsToExpire * 1000 // Convert seconds to milliseconds
    }
    localStorage.setItem(key, JSON.stringify(item))
    return true
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error)
    return false
  }
}

// Promo ID storage constants and helpers (1 hour = 3600 seconds)
export const PROMO_ID_KEY = 'promo_id'
export const PROMO_ID_EXPIRY = 3600

export const setPromoId = promoId => {
  setPartnerData(PROMO_ID_KEY, promoId, PROMO_ID_EXPIRY)
}

export const getPromoId = () => getPartnerData(PROMO_ID_KEY)

export const clearPromoId = () => {
  localStorage.removeItem(PROMO_ID_KEY)
}

// Affiliate ref storage constants and helpers (24 hours = 86400 seconds).
// Captured app-level in main.js so ?ref= survives router navigations that
// drop the query string (e.g. the handleAuth redirect).
export const REF_KEY = 'aff_ref'
export const REF_EXPIRY = 86400

export const setRefCode = ref => {
  setPartnerData(REF_KEY, ref, REF_EXPIRY)
}

export const getRefCode = () => getPartnerData(REF_KEY)

export const clearRefCode = () => {
  localStorage.removeItem(REF_KEY)
}

// Device fingerprint for invite-friend anti-cheat: a stable random id per
// browser profile, sent in the register payload so the backend can flag
// registrations from the same device as the referrer. Never expires.
export const DEVICE_ID_KEY = 'did'

export const getDeviceId = () => {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY)
    if (!deviceId) {
      if (window.crypto && window.crypto.randomUUID) {
        deviceId = window.crypto.randomUUID().replace(/-/g, '')
      } else {
        deviceId =
          Math.random()
            .toString(36)
            .slice(2) +
          Math.random()
            .toString(36)
            .slice(2) +
          Date.now().toString(36)
      }
      localStorage.setItem(DEVICE_ID_KEY, deviceId)
    }
    return deviceId
  } catch (error) {
    console.error('Error reading device id from localStorage:', error)
    return null
  }
}

/**
 * Emit login success event for reactive UI updates
 * Call this after successful login to update all components
 */
export const emitLoginSuccess = () => {
  if (typeof window !== 'undefined') {
    // Use Vue app instance EventBus
    const appElement = document.querySelector('#app')
    // eslint-disable-next-line no-underscore-dangle
    const vueApp = appElement?.__vue__
    if (vueApp && vueApp.$eventBus) {
      vueApp.$eventBus.$emit('user-logged-in')
      console.log('Login success event emitted')
    }
  }
}

/**
 * This function is used for demo purpose route navigation
 * In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 * Please note role field is just for showing purpose it's not used by anything in frontend
 * We are checking role just for ease
 * NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = userRole => {
  if (userRole === 'admin') return '/'
  if (userRole === 'client') return { name: 'access-control' }
  return { name: 'auth-login' }
}

/**
 * Logout user by clearing all authentication data and redirecting
 * @param {Object} router - Vue router instance
 */
export const logoutUser = async router => {
  try {
    console.log('Logging out user')

    // Call logout API to revoke token (don't block on failure)
    await useJwt.logout().catch(() => {})

    // Clear all authentication data from localStorage
    localStorage.removeItem('userData')
    localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
    localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)

    // Clear any other auth-related data
    localStorage.removeItem('userAbilities')
    localStorage.removeItem('userBalance')

    // Emit logout event for reactive updates
    if (typeof window !== 'undefined') {
      // Use Vue app instance EventBus
      const appElement = document.querySelector('#app')
      // eslint-disable-next-line no-underscore-dangle
      const vueApp = appElement?.__vue__
      if (vueApp && vueApp.$eventBus) {
        vueApp.$eventBus.$emit('user-logged-out')
      }
    }

    // Redirect to home page (avoid redundant navigation)
    if (router) {
      // Check if we're not already on the home page to avoid NavigationDuplicated error
      if (router.currentRoute.path !== '/') {
        router.replace('/').catch(err => {
          // Suppress NavigationDuplicated warnings
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error during logout:', err)
          }
        })
      } else {
        // Force page reload to reset all state if already on home page
        window.location.reload()
      }
    } else {
      window.location.href = '/'
    }

    console.log('Logout completed successfully')
  } catch (error) {
    console.error('Error during logout:', error)
    // Still clear local data even if there's an error
    localStorage.clear()
    window.location.href = '/'
  }
}
